import { Box, Button, Grid, Typography } from '@mui/material'
import { type ServiceProviderOption } from 'Pages/CaregiverPortal/Components/ServiceProviderMapSelect'
import { type ChildMemberDetails } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { type service } from 'core/types'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'
import { DragAndDropReorderList, type ReorderItem } from 'Pages/CaregiverPortal/InterestForm/Step6/DragAndDropReorderList'
import { useState, useEffect } from 'react'

export interface RequestSummaryProps {
  isdId: number
  child: ChildMemberDetails
  providers: ServiceProviderOption[]
  service: service
  handleBack: () => void
  submitRequest: (orderedProviderIDs: number[]) => void
}

export function RequestSummary (props: RequestSummaryProps): JSX.Element {
  const [providers, setProviders] = useState<ReorderItem[]>([])
  const [requestSent, setRequestSent] = useState<boolean>(false)

  useEffect(() => {
    const fetchData = (): void => {
      const facilityIds = props.providers.map(p => p.id)
      const list: ReorderItem[] = []
      for (let i = 0; i < facilityIds.length; i++) {
        const fid = facilityIds[i]
        const facility = props.providers.find(f => f.id === fid)
        if (facility == null) {
          continue
        }

        const orderableItem: ReorderItem = {
          id: fid,
          index: i,
          firstLine: facility?.name ?? '',
          secondLine: facility?.todayHours ?? '',
          thirdLine: facility?.distance ?? ''
        }

        list.push(orderableItem)
      }
      setProviders(list)
    }

    fetchData()
  }, [])

  const handleListReordered = async (facilities: ReorderItem[]): Promise<void> => {
    setProviders(facilities)
  }

  function submitRequest (): void {
    setRequestSent(true)
    const orderedProviderIDs = providers.map(p => p.id)
    props.submitRequest(orderedProviderIDs)
  }

  return <>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h5' sx={{ fontWeight: 600 }} component='div'>
          Please review the information and submit the inquiry form.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box key={props.service.id}>
          <Typography variant='h6' component='div'>
            {props.service.name}
          </Typography>
          <Box key={props.child.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant='body1' component='div'>
                {props.child.firstName} {props.child.lastName}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant='body1' component='div' sx={{ mt: '20px', fontWeight: 'bold' }}>
                Preferred Providers
              </Typography>
            </Box>

            {props.providers.length > 1 &&
              <>
                <Typography variant='body1' component='div'>
                  <CustomLabelWithToolTip
                    name='IF_Final_Summary_Rank'
                    isdId={props.isdId ?? 0}
                    defaultLabelText='Due to high demand, we would like you to rank your selected service providers by preference. This will ensure that your interest form will be seen and evaluated by your most desired providers first.'
                  />
                </Typography>
                <Typography variant='body1' component='div' sx={{ mt: '20px' }}>
                  Drag and drop to reorder the list.
                </Typography>
              </>
            }
            {props.providers.length > 0 &&
              <DragAndDropReorderList
                sx={{ mb: '20px' }}
                items={providers}
                onListReordered={async (items) => { await handleListReordered(items) }}
              />
            }
            {props.providers.length === 0 &&
              <Typography variant='body1' component='div' sx={{ mb: '20px' }}>
                I would like someone to reach out to me
              </Typography>
            }
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ mb: '20px', mt: '20px' }}>
            <Button
              name='backButton'
              className='back-button'
              data-testid='back-button'
              onClick={props.handleBack}
              variant='outlined'
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6} sx={{ mb: '20px', mt: '20px' }}>
            <Button
              name='submitRequestButton'
              className='footer-button'
              data-testid='submit-request-button'
              onClick={submitRequest}
              variant='contained'
              disabled={requestSent}
            >
              Submit Request
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </>
}
